import React, { useState, useRef, useEffect } from 'react';
import styles from './Tooltip.module.css';

/**
 * Arrowed Tooltip Component
 * 
 * some hacks are used so when moving from trigger to tooltip, the tooltip remains visible.
 * only works for right placement currently
 * the idea is when moving from left to right, we set a safe zone so the tooltip doesn't hide too early.
 * might need to rethink approach
 * calculation method copied from open source github repo
 */

export type Placement = "top" | "bottom" | "left" | "right" | "bottomLeft" | "topLeft" | "bottomRight";

export interface TooltipProps {
  title: React.ReactNode;
  children: React.ReactNode;
  placement?: Placement;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  title, 
  children, 
  placement = "top",
  className = ""
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  const mouseDirectionRef = useRef({ x: 0, y: 0 });
  const prevMousePosRef = useRef({ x: 0, y: 0 });
  const isMovingToTooltipRef = useRef(false);
  
  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    
    const gap = 10;
    
    let left = 0;
    let top = 0;
    
    switch (placement) {
      case "top":
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        top = triggerRect.top - tooltipRect.height - gap;
        break;
      
      case "bottom":
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        top = triggerRect.bottom + gap;
        break;
      
      case "left":
        left = triggerRect.left - tooltipRect.width - gap;
        top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        break;
      
      case "right":
        left = triggerRect.right + gap;
        top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        break;
        
      case "topLeft":
        left = triggerRect.left;
        top = triggerRect.top - tooltipRect.height - gap;
        break;
        
      case "bottomLeft":
        left = triggerRect.left;
        top = triggerRect.bottom + gap;
        break;
        
      case "bottomRight":
        left = triggerRect.right - tooltipRect.width;
        top = triggerRect.bottom + gap;
        break;
    }
    
    const padding = 10;
    
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    
    if (top < padding) {
      top = padding;
    } else if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }
    
    left += window.scrollX;
    top += window.scrollY;
    
    setTooltipPosition({ top, left });
  };
  
  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(() => {
        calculatePosition();
      });
      
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
      
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [isVisible]);
  
  /**
   * Global mouse movement handler that tracks mouse position and direction
   * to determine when to show/hide the tooltip
   */
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      
      mouseDirectionRef.current = {
        x: mouseX - prevMousePosRef.current.x,
        y: mouseY - prevMousePosRef.current.y
      };
      
      prevMousePosRef.current = { x: mouseX, y: mouseY };
      
      if (!triggerRef.current) return;
      
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();
      
      const isOverTrigger = 
        mouseX >= triggerRect.left && 
        mouseX <= triggerRect.right && 
        mouseY >= triggerRect.top && 
        mouseY <= triggerRect.bottom;
      
      let isOverTooltip = false;
      if (tooltipRect) {
        isOverTooltip = 
          mouseX >= tooltipRect.left && 
          mouseX <= tooltipRect.right && 
          mouseY >= tooltipRect.top && 
          mouseY <= tooltipRect.bottom;
        
        const buffer = 15;
        if (!isOverTooltip) {
          isOverTooltip = 
            mouseX >= tooltipRect.left - buffer && 
            mouseX <= tooltipRect.right + buffer && 
            mouseY >= tooltipRect.top - buffer && 
            mouseY <= tooltipRect.bottom + buffer;
        }
      }
      
      if (isOverTrigger || isOverTooltip) {
        setIsVisible(true);
        isMovingToTooltipRef.current = false;
        return;
      }
      
      if (isVisible && tooltipRect) {
        const isMovingToTooltip = isMovingTowardTooltip(mouseX, mouseY, mouseDirectionRef.current, tooltipRect, placement);
        
        if (isMovingToTooltip) {
          isMovingToTooltipRef.current = true;
        } else if (isMovingToTooltipRef.current) {
          setIsVisible(false);
          isMovingToTooltipRef.current = false;
        } else {
          setIsVisible(false);
        }
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible, placement]);
  
  /**
   * Determines if the mouse is moving toward the tooltip based on movement direction,
   * cursor position, and tooltip placement
   */
  const isMovingTowardTooltip = (
    mouseX: number, 
    mouseY: number, 
    direction: { x: number, y: number }, 
    tooltipRect: DOMRect,
    placement: Placement
  ): boolean => {
    const tooltipCenterX = tooltipRect.left + tooltipRect.width / 2;
    const tooltipCenterY = tooltipRect.top + tooltipRect.height / 2;
    
    const distToLeft = Math.abs(mouseX - tooltipRect.left);
    const distToRight = Math.abs(mouseX - tooltipRect.right);
    const distToTop = Math.abs(mouseY - tooltipRect.top);
    const distToBottom = Math.abs(mouseY - tooltipRect.bottom);
    
    const isCloseToTooltip = 
      distToLeft < 100 || 
      distToRight < 100 || 
      distToTop < 100 || 
      distToBottom < 100;
    
    if (!isCloseToTooltip) return false;
    
    switch (placement) {
      case 'right':
        return direction.x > 0 || (
          Math.abs(direction.y) > Math.abs(direction.x) && 
          mouseX > tooltipRect.left - 150 &&
          mouseY > tooltipRect.top - 50 &&
          mouseY < tooltipRect.bottom + 50
        );
        
      case 'left':
        return direction.x < 0 || (
          Math.abs(direction.y) > Math.abs(direction.x) && 
          mouseX < tooltipRect.right + 150 &&
          mouseY > tooltipRect.top - 50 &&
          mouseY < tooltipRect.bottom + 50
        );
        
      case 'bottom':
      case 'bottomLeft':
      case 'bottomRight':
        return direction.y > 0 || (
          Math.abs(direction.x) > Math.abs(direction.y) && 
          mouseY > tooltipRect.top - 150 &&
          mouseX > tooltipRect.left - 50 &&
          mouseX < tooltipRect.right + 50
        );
        
      case 'top':
      case 'topLeft':
        return direction.y < 0 || (
          Math.abs(direction.x) > Math.abs(direction.y) && 
          mouseY < tooltipRect.bottom + 150 &&
          mouseX > tooltipRect.left - 50 &&
          mouseX < tooltipRect.right + 50
        );
        
      default:
        const vectorToTooltip = {
          x: tooltipCenterX - mouseX,
          y: tooltipCenterY - mouseY
        };
        
        const dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
        const normalizedDir = {
          x: direction.x / (dirLength || 1),
          y: direction.y / (dirLength || 1)
        };
        
        const tooltipLength = Math.sqrt(vectorToTooltip.x * vectorToTooltip.x + vectorToTooltip.y * vectorToTooltip.y);
        const normalizedTooltip = {
          x: vectorToTooltip.x / (tooltipLength || 1),
          y: vectorToTooltip.y / (tooltipLength || 1)
        };
        
        const dotProduct = normalizedDir.x * normalizedTooltip.x + normalizedDir.y * normalizedTooltip.y;
        
        return dotProduct > 0;
    }
  };
  
  const handleMouseEnter = (e: React.MouseEvent) => {
    prevMousePosRef.current = { x: e.clientX, y: e.clientY };
    setIsVisible(true);
  };
  
  return (
    <div ref={containerRef} className={className}>
      <div 
        ref={triggerRef}
        className={styles.trigger}
        onMouseEnter={handleMouseEnter}
      >
        {children}
      </div>
      
      {isVisible && title && (
        <div 
          ref={tooltipRef}
          className={`${styles.container} ${styles[placement]}`}
          style={{
            position: 'fixed',
            left: `${tooltipPosition.left}px`,
            top: `${tooltipPosition.top}px`,
            zIndex: 1000
          }}
          role="tooltip"
        >
          <div className={styles.content}>{title}</div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;